import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Image as RNImage, KeyboardAvoidingView, Modal, Platform, ScrollView, TextInput, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useToast } from 'react-native-toast-notifications';
import { Ionicons } from '@expo/vector-icons';
import type { SignatureInkHandle } from 'react-native-signature-ink';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import SignatureView from '../../../../components/SignatureView';
import IconButton from '../../../../components/IconButton';
import Icon from '@/components/Icon';
import api from '../../../../utils/api';
import { EvidenceLevel, isOutsideServiceHours, takeAndUploadPhoto, useAccessAuthorizationConfig } from './accessAuthorizationConfig';

const SignatureInk = Platform.OS !== 'web' ? require('react-native-signature-ink').SignatureInk : null;


type Photo = { uuid: string; url: string };


/**
 * Registro del ingreso de un proveedor con responsiva: captura la evidencia que pide la
 * comunidad (`access_authorization.entry_evidence`) y hace POST .../entries.
 * `source`: qr si se llegó escaneando, search si se buscó en la lista.
 */
export default function GuardAuthorizationEntryModal({ visible, authorization, communityId, source, onRequestClose, onRegistered }: {
  visible: boolean;
  authorization: any;
  communityId: string;
  source: 'qr' | 'search';
  onRequestClose: () => void;
  onRegistered: (log: any) => void;
}) {
  const insets = useSafeAreaInsets();
  const toast = useToast();
  const { evidence, serviceHours } = useAccessAuthorizationConfig(communityId);

  const [photos, setPhotos] = useState<Photo[]>([]);
  const [peopleCount, setPeopleCount] = useState(1);
  const [plateNumber, setPlateNumber] = useState('');
  const [comments, setComments] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const [uploadingSlot, setUploadingSlot] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Los toasts quedan debajo del Modal nativo: los errores se muestran dentro de la pantalla
  const [attempted, setAttempted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setPhotos([]);
    setPeopleCount(1);
    setPlateNumber(authorization?.vehicle_info?.plate_number ?? '');
    setComments('');
    setSignature(null);
    setAttempted(false);
    setSubmitError(null);
  }, [visible, authorization?.uuid]);

  if (!authorization) return null;

  const isVehicular = authorization.access_type === 'vehicular';
  const show = (level: EvidenceLevel) => level !== 'off';
  const outsideHours = isOutsideServiceHours(serviceHours);

  async function capture() {
    setUploadingSlot('photo');
    try {
      const photo = await takeAndUploadPhoto(communityId);
      if (photo) setPhotos(prev => [...prev, photo]);
    } catch (err: any) {
      setSubmitError(err?.response?.data?.message ?? 'No se pudo subir la foto, intenta de nuevo');
    } finally {
      setUploadingSlot(null);
    }
  }

  // Lo mismo que valida la API, para marcar lo que falta antes de enviar
  const missing = {
    photos: evidence.photos === 'required' && photos.length === 0,
    signature: evidence.signature === 'required' && !signature,
  };
  const missingLabels = [
    missing.photos && 'al menos una foto',
    missing.signature && 'firma del proveedor',
  ].filter(Boolean) as string[];

  async function submit() {
    setAttempted(true);
    setSubmitError(null);
    if (missingLabels.length > 0) return;

    setIsSubmitting(true);
    try {
      const { data } = await api.post(`/communities/${communityId}/access-authorizations/${authorization.uuid}/entries`, {
        source,
        photo_uids: photos.map(p => p.uuid),
        people_count: peopleCount,
        signature: signature ?? undefined,
        vehicle_plate_number: isVehicular && plateNumber.trim() ? plateNumber.trim().toUpperCase() : undefined,
        comments: comments.trim() || undefined,
      });

      toast.show('Entrada registrada', { type: 'success' });
      onRegistered(data);
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      const message = errors ? Object.values(errors).flat()[0] : err?.response?.data?.message;
      setSubmitError(String(message ?? 'No se pudo registrar la entrada'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal visible={visible} onRequestClose={onRequestClose} statusBarTranslucent animationType='slide'>
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={tw`flex-1 bg-white`}>
        <ScrollView style={tw`flex-1 pt-[${insets.top}px]`} contentContainerStyle={tw`px-5 pb-[${insets.bottom + 40}px]`} keyboardShouldPersistTaps='handled'>
          <View style={tw`row justify-between items-center mt-5 mb-4`}>
            <Text style={tw`text-primary text-lg font-600`}>Registrar entrada</Text>
            <TouchableOpacity onPress={onRequestClose}>
              <Icon name='close' size={26} color={tw.color('red')} />
            </TouchableOpacity>
          </View>

          <View style={tw`p-4 bg-gray-50 rounded-xl mb-4`}>
            <Text style={tw`text-gray-800 text-lg font-500`}>{authorization.provider_name || authorization.description || authorization.service_type}</Text>
            <Text style={tw`text-gray-500 mt-1`}>{authorization.unit?.name} · {authorization.access_type_str}</Text>
          </View>

          {outsideHours && <OutsideHoursBanner />}

          {isVehicular && (
            <Section title='Número de placas'>
              <TextInput
                value={plateNumber}
                onChangeText={setPlateNumber}
                autoCapitalize='characters'
                placeholder='ABC1234'
                style={tw`border border-gray-200 rounded-xl px-4 py-3 text-lg tracking-widest`}
              />
            </Section>
          )}

          <Section title='Personas que entran'>
            <View style={tw`row items-center`}>
              <CounterButton icon='remove' disabled={peopleCount <= 1} onPress={() => setPeopleCount(c => Math.max(1, c - 1))} />
              <Text style={tw`text-2xl font-600 text-gray-800 mx-6`}>{peopleCount}</Text>
              <CounterButton icon='add' disabled={peopleCount >= 30} onPress={() => setPeopleCount(c => Math.min(30, c + 1))} />
            </View>
          </Section>

          {show(evidence.photos) && (
            <Section title='Fotos' level={evidence.photos} error={attempted && missing.photos}>
              <Text style={tw`text-gray-400 text-sm mb-3`}>Las que pida la administración: placas, identificaciones, vehículo, carga…</Text>
              <View style={tw`row flex-wrap gap-3`}>
                {photos.map(photo => (
                  <PhotoSlot
                    key={photo.uuid}
                    photo={photo}
                    uploading={false}
                    onCapture={capture}
                    onRemove={() => setPhotos(prev => prev.filter(p => p.uuid !== photo.uuid))}
                  />
                ))}
                {photos.length < 30 && (
                  <PhotoSlot photo={null} uploading={uploadingSlot === 'photo'} onCapture={capture} onRemove={() => {}} />
                )}
              </View>
            </Section>
          )}

          {show(evidence.signature) && (
            <Section title='Firma del proveedor' level={evidence.signature} error={attempted && missing.signature}>
              {signature ? (
                <>
                  <SignatureView xml={signature} label='' style={tw`mt-0`} />
                  <TouchableOpacity onPress={() => setSignature(null)} style={tw`mt-2 self-start`}>
                    <Text style={tw`text-primary font-500`}>Volver a firmar</Text>
                  </TouchableOpacity>
                </>
              ) : (
                <TouchableOpacity
                  onPress={() => setSigning(true)}
                  style={tw`border-2 border-dashed border-primary rounded-xl h-24 items-center justify-center`}
                >
                  <Text style={tw`text-primary font-500`}>Tomar firma</Text>
                </TouchableOpacity>
              )}
            </Section>
          )}

          <Section title='Comentarios'>
            <TextInput
              value={comments}
              onChangeText={setComments}
              placeholder='Opcional'
              multiline
              maxLength={255}
              style={tw`border border-gray-200 rounded-xl px-4 py-3 min-h-20`}
            />
          </Section>

          {attempted && missingLabels.length > 0 && (
            <View style={tw`p-3 mt-2 bg-red-50 border border-red-200 rounded-xl`}>
              <Text style={tw`text-red-700 font-500`}>Falta: {missingLabels.join(', ')}</Text>
            </View>
          )}

          {!!submitError && (
            <View style={tw`p-3 mt-2 bg-red-50 border border-red-200 rounded-xl`}>
              <Text style={tw`text-red-700 font-500`}>{submitError}</Text>
            </View>
          )}

          <IconButton
            title={isSubmitting ? 'Registrando...' : 'Registrar entrada'}
            icon='check'
            disabled={isSubmitting || !!uploadingSlot}
            onPress={submit}
            style={tw`mt-4`}
          />
        </ScrollView>
      </KeyboardAvoidingView>

      <SignatureModal
        visible={signing}
        onRequestClose={() => setSigning(false)}
        onSigned={svg => {
          setSignature(svg);
          setSigning(false);
        }}
      />
    </Modal>
  );
}


export function OutsideHoursBanner() {
  return (
    <View style={tw`row items-center p-3 mb-4 bg-amber-50 border border-amber-200 rounded-xl`}>
      <Ionicons name='time-outline' size={20} color={tw.color('amber-700')} />
      <Text style={tw`text-amber-700 ml-2 flex-1`}>Fuera del horario de servicio para proveedores</Text>
    </View>
  );
}


function Section({ title, level, error, children }: { title: string; level?: EvidenceLevel; error?: boolean; children: React.ReactNode }) {
  return (
    <View style={tw.style(`mb-5`, error && `p-3 -mx-3 bg-red-50 rounded-xl`)}>
      <View style={tw`row items-center mb-2`}>
        <Text style={tw.style(`font-500`, error ? `text-red-700` : `text-gray-600`)}>{title}</Text>
        {level === 'required' && <Text style={tw`text-red ml-1`}>*</Text>}
        {level === 'optional' && <Text style={tw`text-gray-400 text-sm ml-2`}>(opcional)</Text>}
        {error && <Text style={tw`text-red-700 text-sm ml-2`}>Obligatorio</Text>}
      </View>
      {children}
    </View>
  );
}


function PhotoSlot({ photo, label, uploading, onCapture, onRemove }: {
  photo: Photo | null;
  label?: string;
  uploading: boolean;
  onCapture: () => void;
  onRemove: () => void;
}) {
  if (uploading) {
    return (
      <View style={tw`w-28 h-28 rounded-xl bg-gray-50 items-center justify-center`}>
        <ActivityIndicator color={tw.color('primary')} />
      </View>
    );
  }

  if (photo) {
    return (
      <View>
        <RNImage source={{ uri: photo.url }} style={tw`w-28 h-28 rounded-xl`} />
        <TouchableOpacity
          onPress={onRemove}
          style={tw`absolute -top-2 -right-2 bg-white rounded-full border border-gray-200 w-7 h-7 items-center justify-center`}
        >
          <Ionicons name='close' size={16} color='#374151' />
        </TouchableOpacity>
        {label && <Text style={tw`text-xs text-gray-500 mt-1 text-center`}>{label}</Text>}
      </View>
    );
  }

  return (
    <View>
      <TouchableOpacity
        onPress={onCapture}
        style={tw`w-28 h-28 rounded-xl border-2 border-dashed border-primary items-center justify-center`}
      >
        <Ionicons name='camera-outline' size={30} color={tw.color('primary')} />
        <Text style={tw`text-xs text-primary mt-1`}>Tomar foto</Text>
      </TouchableOpacity>
      {label && <Text style={tw`text-xs text-gray-500 mt-1 text-center`}>{label}</Text>}
    </View>
  );
}


function CounterButton({ icon, disabled, onPress }: { icon: 'add' | 'remove'; disabled: boolean; onPress: () => void }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      style={tw.style(`w-12 h-12 rounded-full border-2 border-primary items-center justify-center`, disabled && `opacity-30`)}
    >
      <Ionicons name={icon} size={24} color={tw.color('primary')} />
    </TouchableOpacity>
  );
}


function SignatureModal({ visible, onRequestClose, onSigned }: {
  visible: boolean;
  onRequestClose: () => void;
  onSigned: (svg: string) => void;
}) {
  const insets = useSafeAreaInsets();
  const signatureRef = useRef<SignatureInkHandle>(null);
  const [emptyError, setEmptyError] = useState(false);

  useEffect(() => { if (visible) setEmptyError(false); }, [visible]);

  async function save() {
    if (await signatureRef.current?.isEmpty()) {
      setEmptyError(true);
      return;
    }
    const svg = await signatureRef.current?.toSvg();
    if (svg) onSigned(svg);
  }

  return (
    <Modal visible={visible} onRequestClose={onRequestClose} statusBarTranslucent animationType='slide'>
      <View style={tw`flex-1 bg-white px-5 pt-[${insets.top + 20}px] pb-[${insets.bottom + 20}px]`}>
        <View style={tw`row justify-between items-center mb-4`}>
          <Text style={tw`text-primary text-lg font-600`}>Firma del proveedor</Text>
          <TouchableOpacity onPress={onRequestClose}>
            <Icon name='close' size={26} color={tw.color('red')} />
          </TouchableOpacity>
        </View>

        <View style={tw`flex-1 rounded-xl border border-gray-300 overflow-hidden mb-4`}>
          {SignatureInk ? (
            <SignatureInk
              ref={signatureRef}
              style={{ flex: 1 }}
              showBaseline
              showToolbar
              penColor='#111'
              toolbarButtons={['clear']}
            />
          ) : (
            <View style={tw`flex-1 items-center justify-center`}>
              <Text style={tw`text-gray-400`}>La firma no está disponible en web</Text>
            </View>
          )}
        </View>

        {emptyError && (
          <Text style={tw`text-red-700 text-center mb-3`}>El proveedor debe firmar dentro del recuadro</Text>
        )}

        <IconButton title='Guardar firma' icon='check' onPress={save} />
      </View>
    </Modal>
  );
}
